import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Sparkles } from 'lucide-react';
import heroImage from '@/assets/hero-trades.jpg';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  badge?: string;
  showRating?: boolean;
  rating?: number;
  reviewCount?: number;
  ctaText?: string;
  ctaHref?: string;
  size?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'center';
  backgroundImage?: string;
  children?: React.ReactNode;
}

export const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  badge,
  showRating = false,
  rating = 4.9,
  reviewCount = 100,
  ctaText,
  ctaHref,
  size = 'md',
  align = 'left',
  backgroundImage,
  children,
}) => {
  const sizeClasses = {
    sm: 'py-16 lg:py-20',
    md: 'py-20 lg:py-28',
    lg: 'py-28 lg:py-36',
  };

  const titleSizeClasses = {
    sm: 'text-3xl lg:text-4xl',
    md: 'text-4xl lg:text-5xl',
    lg: 'text-5xl lg:text-6xl',
  };

  const bgSrc = backgroundImage || heroImage;

  return (
    <section className={`relative overflow-hidden bg-foreground ${sizeClasses[size]}`}>
      {/* Background image with dark overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/95 to-foreground/80" />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
          {/* Badge */}
          {badge && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-5"
            >
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-background/80 border border-background/20 rounded-full">
                <Sparkles className="h-3.5 w-3.5" />
                {badge}
              </span>
            </motion.div>
          )}

          {/* Rating */}
          {showRating && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className={`flex items-center gap-2.5 mb-5 ${align === 'center' ? 'justify-center' : ''}`}
            >
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(rating)
                        ? 'text-primary fill-primary'
                        : 'text-background/30'
                    }`}
                  />
                ))}
              </div>
              <span className="font-semibold text-sm text-background">{rating}</span>
              <span className="text-background/50 text-sm">({reviewCount}+ reviews)</span>
            </motion.div>
          )}

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`font-heading font-bold tracking-tight text-background leading-[1.1] mb-5 ${titleSizeClasses[size]}`}
          >
            {title}
          </motion.h1>

          {/* Subtitle */}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className={`text-lg text-background/60 max-w-xl leading-relaxed ${align === 'center' ? 'mx-auto' : ''}`}
            >
              {subtitle}
            </motion.p>
          )}

          {/* CTA */}
          {ctaText && ctaHref && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="mt-8"
            >
              <a
                href={ctaHref}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-full hover:bg-primary/90 transition-colors group"
              >
                {ctaText}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          )}

          {/* Custom children */}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="mt-8"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
